// Aberturas da PILHA de overlays da ficha (docs/plan/tv-ficha-overlay.md, P1 VOD).
//
// Concentra a captura de contexto que os call sites do card NÃO devem repetir:
//   - `restoreFocusKey`: o card focado AGORA (getCurrentFocusKey), pra onde o foco
//     volta quando a ficha fecha.
//   - `restoreSafeKey`: o `nav-*` da origem (safeKeyForOrigin do pathname), a rede
//     de segurança se o card sumiu.
// Nenhuma destas funções NAVEGA de rota — só empilham no store. É isso que mantém
// a origem montada (path parado) e conserta o Voltar-remonta.

import { getCurrentFocusKey } from '@app-iptv/ui'
import { createContext, useCallback, useContext } from 'react'
import { useLocation } from 'react-router-dom'

import {
  getStack,
  isDetailLayer,
  openDetail,
  pop,
  pushPlayer,
  useStack,
} from '../detail-overlay-store'
import { safeKeyForOrigin } from '../lib/origin-safe-key'

import { startEntryFocus } from './useEntryFocus'

// ==================== POSSE DE CAMADA ====================
//
// POR QUE ISTO PRECISA EXISTIR
// `MovieDetailsScreen` e `SeriesDetailsScreen` NÃO são rotas: são camadas do
// `DetailOverlayHost`, irmão do RouterProvider. O host faz `stack.map(renderLayer)`
// e a camada de BAIXO não desmonta quando o player entra por cima. Cada tela
// registra o SEU `window.addEventListener('keydown')` e chama `preventDefault()`,
// nunca `stopImmediatePropagation()` — que é o único que impediria outro listener
// no mesmo alvo. Ou seja: com a ficha e o player montados juntos, os DOIS
// listeners recebem a mesma tecla.
//
// Enquanto a ficha usava o VERMELHO (403) e o player o AMARELO (405) isso não
// aparecia. O mapa novo põe FAVORITAR no amarelo nos dois — um toque dispararia
// o toggle duas vezes e voltaria ao estado inicial. O sintoma em produção seria
// "favoritar parou de funcionar no player de filme e de série", que não parece
// um bug de camada nenhum.
//
// O guard é o ÍNDICE na pilha, lido de forma SÍNCRONA dentro do keydown — a
// mesma disciplina do árbitro de Back. Ancorar em estado PRÓPRIO (a pilha) em
// vez de num proxy derivado de render deixa o guard imune à ordem de effects.

/**
 * Índice desta camada na pilha de overlays. `null` = árvore ROTEADA (a tela não
 * é camada nenhuma). O `DetailOverlayHost` provê o índice em cada camada.
 */
export const OverlayLayerIndex = createContext<number | null>(null)

/**
 * Esta camada é a DONA das teclas de tela agora? Leitura SÍNCRONA — pra rodar
 * dentro do handler de keydown, nunca a partir de um closure de render.
 *
 * Rota (`index === null`) só é dona com a pilha VAZIA: com um overlay aberto,
 * quem manda é o overlay. Camada é dona quando é o TOPO.
 */
export function ownsScreenKeys(index: number | null): boolean {
  const stack = getStack()
  if (index === null) return stack.length === 0
  return index === stack.length - 1
}

/**
 * Versão REATIVA do mesmo predicado, pra decisões de RENDER (ex.: só a camada do
 * topo pinta a barra de dicas). Não use como guard de tecla: o guard tem que ler
 * a pilha no instante do keydown, não no último render.
 */
export function useIsTopLayer(): boolean {
  const index = useContext(OverlayLayerIndex)
  const stack = useStack()
  if (index === null) return stack.length === 0
  return index === stack.length - 1
}

/**
 * Fecha a camada do topo pela MESMA máquina do Back de hardware: `pop()` + o
 * restore de foco GUARDADO (`startEntryFocus`, que só foca key REGISTRADA, prova
 * o pouso pelo `[data-focused]` e cai no `safeKey` se o card de origem sumiu).
 *
 * É o PONTO ÚNICO de "fechar overlay": o árbitro de Back (DetailOverlayHost) E os
 * botões "Voltar" das fichas chamam ISTO — NUNCA `pop()` cru. O pop sozinho só
 * muta a pilha e revela a origem SEM `[data-focused]` vivo (a focusKey do botão
 * que fechou morre com o unmount) → `smartNavigate` aborta ("missing current
 * component") → setas mortas. Fechar por OK/Enter contornava o restore (que só
 * existia no caminho da tecla Back), e como a tela de indisponível tem 1 focável
 * só, era o caminho PRIMÁRIO pro D-pad morto, não edge.
 *
 * Ficha removida → devolve o foco pro card de origem (`restoreFocusKey`), com o
 * `nav-*` (`restoreSafeKey`) de rede. Player removido → a ficha reaparece e
 * refoca o próprio Play (responsabilidade da tela) → no-op aqui.
 *
 * Devolve o cancelador do restore em curso (agenda rAF+timers): o árbitro cancela
 * um restore anterior antes de disparar outro; o botão descarta (fechar por OK é
 * terminal — a ficha desmonta).
 */
export function closeTopLayer(): (() => void) | null {
  const removed = pop()
  if (removed && isDetailLayer(removed)) {
    return startEntryFocus({
      targetKey: removed.restoreFocusKey,
      safeKey: removed.restoreSafeKey,
    })
  }
  return null
}

/**
 * Abre a ficha de FILME por cima da origem (card→ficha). Captura o foco atual (o
 * card) e o `nav-*` da origem. Devolve o handler pros `onEnterPress`/`onPress` dos
 * cards. NÃO navega — a origem fica montada, path parado.
 */
export function useOpenMovieDetail(): (id: string) => void {
  const location = useLocation()
  return useCallback(
    (id: string) => {
      openDetail({
        kind: 'movie-detail',
        id,
        restoreFocusKey: getCurrentFocusKey() || '',
        restoreSafeKey: safeKeyForOrigin(location.pathname),
      })
    },
    [location.pathname],
  )
}

/**
 * Play de dentro da ficha: empurra o player POR CIMA da ficha (que fica montada,
 * com scroll/estado preservados). Função pura de store — sem captura de foco (o
 * player usa `pause()`, não spatial nav). No fecho, o Back volta pra ficha.
 */
export function pushVodPlayer(sourceId: string, resumeAt?: number): void {
  if (resumeAt !== undefined && resumeAt > 0) {
    pushPlayer({ kind: 'vod-player', sourceId, resumeAt })
  } else {
    pushPlayer({ kind: 'vod-player', sourceId })
  }
}

/**
 * Recomendação clicada DENTRO da ficha: TROCA a ficha do topo pela do novo filme,
 * herdando as keys de restauração da origem. Assim a pilha de FICHAS nunca passa
 * de 1 — duas fichas empilhadas colidiriam nas mesmas focusKeys (`btn-play` etc.),
 * já que o Norigin registra por key GLOBAL. Back de uma ficha aberta por recomenda-
 * ção volta pra origem (não acumula histórico de fichas) — decisão registrada no
 * relatório do P1. `pop()`+`openDetail()` no MESMO event handler são batcheados
 * pelo React 18 (o host renderiza uma vez com a pilha final); com o host keando as
 * camadas por índice, a ficha do índice 0 é REUSADA (troca só o `id`, sem flash).
 */
export function replaceMovieDetail(id: string): void {
  const stack = getStack()
  const top = stack[stack.length - 1]
  const restore =
    top && isDetailLayer(top)
      ? { restoreFocusKey: top.restoreFocusKey, restoreSafeKey: top.restoreSafeKey }
      : { restoreFocusKey: '', restoreSafeKey: safeKeyForOrigin(null) }
  pop()
  openDetail({ kind: 'movie-detail', id, ...restore })
}

// ============================ SÉRIE (P2) ============================
// Espelho exato do bloco de FILME acima. A ficha de série e o player de episódio
// viram camadas da MESMA pilha; a única especificidade da série (auto-advance) é a
// TRANSIÇÃO de episódio, que usa `replacePlayer` do store direto na tela do player
// (não aqui). O plano dizia que isso preservava o guard v7 anti-duplo-advance: não
// preservou (o v7 dependia da lane do `navigate()`; a cascata de 16/08). O que segura
// hoje é o aviso pertencer à SESSÃO, na própria tela (SeriesPlayerScreen). Ver
// docs/plan/tv-ficha-overlay.md §4-Caso B e a nota de 28/09/2026 no risco 5.

/**
 * Abre a ficha de SÉRIE por cima da origem (card→ficha). Captura o card focado + o
 * `nav-*` da origem, igual ao filme. NÃO navega — a origem fica montada, path parado
 * (é o que devolve scroll+DOM+foco no Voltar sem remontar a grade de séries).
 */
export function useOpenSeriesDetail(): (id: string) => void {
  const location = useLocation()
  return useCallback(
    (id: string) => {
      openDetail({
        kind: 'series-detail',
        id,
        restoreFocusKey: getCurrentFocusKey() || '',
        restoreSafeKey: safeKeyForOrigin(location.pathname),
      })
    },
    [location.pathname],
  )
}

/**
 * Play de um episódio de dentro da ficha de série: empurra o player POR CIMA da
 * ficha (que fica montada com temporada/episódio/scroll preservados). No fecho, o
 * Back volta pra ficha (não pro catálogo). Função pura de store — sem captura de
 * foco (o player usa `pause()`).
 */
export function pushSeriesPlayer(seriesId: string, episodeId: string, resumeAt?: number): void {
  if (resumeAt !== undefined && resumeAt > 0) {
    pushPlayer({ kind: 'series-player', seriesId, episodeId, resumeAt })
  } else {
    pushPlayer({ kind: 'series-player', seriesId, episodeId })
  }
}

/**
 * Recomendação clicada DENTRO da ficha de série: TROCA a ficha do topo pela da nova
 * série, herdando as keys de restauração da origem (mesma razão do filme — a pilha
 * de FICHAS nunca passa de 1 pra não colidir focusKeys globais). Ver replaceMovieDetail.
 */
export function replaceSeriesDetail(id: string): void {
  const stack = getStack()
  const top = stack[stack.length - 1]
  const restore =
    top && isDetailLayer(top)
      ? { restoreFocusKey: top.restoreFocusKey, restoreSafeKey: top.restoreSafeKey }
      : { restoreFocusKey: '', restoreSafeKey: safeKeyForOrigin(null) }
  pop()
  openDetail({ kind: 'series-detail', id, ...restore })
}
